/**
 * Shared shaping of the `canUseTool` permission result for an
 * `AskUserQuestion` answer — used by BOTH gates (the desktop GUI's
 * `AskUserQuestionGate` in `crucible-gui/src/main/ask-user-question-gate.ts`
 * and the cloud `CloudAskUserQuestionGate` in this directory).
 *
 * Issue #3158 — why this module exists:
 *
 *   The SDK treats `updatedInput` as a WHOLESALE REPLACEMENT of the
 *   tool input, not a merge. Both gates used to resolve the held-open
 *   permission Promise with `{ behavior: 'allow', updatedInput:
 *   { answers } }` — dropping the original `questions` array. The
 *   bundled Claude CLI then ran the tool with `questions: undefined`
 *   and crashed while building the tool_result:
 *
 *     // CLI 2.1.126 (bundled with SDK 0.2.126), minified:
 *     async call({questions:H,answers:_={},annotations:q},K){
 *       return{data:{questions:H,answers:_,...}}}
 *     mapToolResultToToolResultBlockParam({questions:H,answers:_,...},K){
 *       return{...,content:`User has answered your questions: ${H.map(...)}...`}}
 *
 *   `answers` has a destructuring default; `questions` does not. With
 *   `questions` stripped, `H.map` throws — and because the packaged
 *   Foundry app spawns the SDK's Bun-compiled native `claude` binary
 *   (JavaScriptCore, not V8 — see `resolveClaudeBinaryPath` in
 *   `agent-adapter.ts`), the agent-visible tool error is JSC's
 *   phrasing: `undefined is not an object (evaluating 'H.map')`.
 *   Every answered decision gate errored and the agent fell back to
 *   plain-text questions. The same unguarded `.map` exists in CLI
 *   2.3.x (SDK 0.3.207), so the input must stay intact there too.
 *
 * The fix: always resolve `allow` with the FULL original input spread
 * under the answers (`{ ...input, answers }`), and fail closed to a
 * structured `deny` for the two shapes that cannot round-trip:
 *
 *   1. `answers` carrying the renderer's schema-error sentinel
 *      (`ASK_USER_QUESTION_SCHEMA_ERROR_KEY`, see #551) — the crafted
 *      rejection message must reach the model as a deny. Feeding it
 *      through `allow` either crashed the CLI (missing `questions`)
 *      or silently dropped the message (over-cap `questions`).
 *   2. `input.questions` not being an array — the CLI's result mapper
 *      calls `.map` on it unconditionally, so allowing the call would
 *      reintroduce the #3158 crash.
 */
/**
 * Sentinel key on the `answers` payload the renderer uses to tell
 * the gate that an `AskUserQuestion` tool call was structurally
 * invalid and could not be rendered (#551). Owned here (main/runtime
 * side) so both gates and the renderer share one definition;
 * `crucible-ui`'s `ChatMessageComponents` re-exports it.
 */
export const ASK_USER_QUESTION_SCHEMA_ERROR_KEY = '__askq_schema_error__';
/**
 * Shape the permission result for a delivered `AskUserQuestion`
 * answer.
 *
 * Args:
 *   input: The ORIGINAL tool input the SDK passed to `canUseTool`.
 *   answers: Renderer-supplied answers keyed by question text (or the
 *     #551 schema-error sentinel).
 *
 * Returns:
 *   `{ behavior: 'allow', updatedInput: { ...input, answers } }` for a
 *   well-formed call, or `{ behavior: 'deny', message }` when the call
 *   cannot safely round-trip through the CLI's tool execution (schema
 *   sentinel present, or `questions` is not an array).
 */
export function buildAskUserQuestionPermissionResult(input, answers) {
    const sentinel = answers[ASK_USER_QUESTION_SCHEMA_ERROR_KEY];
    if (sentinel !== undefined) {
        return { behavior: 'deny', message: schemaErrorDenyMessage(sentinel) };
    }
    if (!Array.isArray(input['questions'])) {
        return {
            behavior: 'deny',
            message: 'AskUserQuestion could not be completed: the tool input was missing a valid ' +
                '`questions` array, so the answer cannot be attached to it. Re-emit the question ' +
                'as { questions: [{ question, header, options: [{ label, description }] }, ...] } ' +
                'with 1-4 questions.',
        };
    }
    return { behavior: 'allow', updatedInput: { ...input, answers } };
}
/**
 * Extract the model-facing message from the renderer's schema-error
 * sentinel value (a JSON blob built by `buildSchemaErrorAnswers` in
 * `crucible-ui`). Falls back to the raw value when the blob does not
 * parse — the raw JSON is still a usable diagnostic for the model.
 */
function schemaErrorDenyMessage(sentinel) {
    const raw = Array.isArray(sentinel) ? sentinel.join(' ') : sentinel;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            const message = parsed.message;
            if (typeof message === 'string' && message.length > 0)
                return message;
        }
    }
    catch {
        // Not JSON — fall through to the raw sentinel value.
    }
    return raw;
}
//# sourceMappingURL=answer-permission-result.js.map